import IconButton from '@/global/components/Button/IconButton'
import { Checkbox } from '@/global/components/Checkbox'
import { optionsColumn } from '@/global/components/DataGrid/hooks/useDataGridPro'
import { GridColDefPro } from '@/global/components/DataGrid/utils/types'
import HorizontalDivider from '@/global/components/Divider/HorizontalDivider'
import { TextField } from '@/global/components/TextField'
import Grid from '@/global/components/ui/Grid'
import ContentGuide from '@/global/guides/ContentGuide'
import { cn } from '@/lib/utils'
import { PreviewEspelho, compactarJornada } from '@acme/core'
import IconeAjuda from 'global/components/IconeAjuda'
import { PlusIcon, Trash2Icon } from 'lucide-react'
import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { TabelaHorarios } from 'service/api'

export default function PreviewEspelhoForm() {
    const { watch, setValue } = useFormContext()

    const horarios = watch('horarios')
    const totalSemanal = watch('totSem')
    const previewEspelho = parsePreviewEspelho(watch('previewEspelho'))
    const lastUpdate = watch('updatedAt')

    const memo = useMemo(() => {
        const rows = previewEspelho?.horarios ?? compactarJornada(horarios)
        const columns = mapColumnsEntradasSaidas(previewEspelho, horarios)

        return {
            rows: rows.map(({ entradasSaidas, ...rest }, id) => ({
                ...entradasSaidas,
                ...rest,
                id,
            })),
            columns: [
                {
                    ...optionsColumn,
                    editable: true,
                    field: 'dia',
                    headerName: 'DIA',
                    flex: 1,
                    minWidth: 100,
                },
                ...columns,
                {
                    ...optionsColumn,
                    editable: true,
                    field: 'total',
                    headerName: 'TOT',
                    width: 100,
                },
                ...(!previewEspelho
                    ? []
                    : [
                          {
                              ...optionsColumn,
                              field: '_excluir',
                              width: 50,
                              renderCell: ({ row: { id } }: any) => (
                                  <IconButton
                                      title="Excluir linha"
                                      className="mx-auto"
                                      size="small"
                                      onClick={() => {
                                          setValue('previewEspelho', {
                                              ...previewEspelho,
                                              horarios:
                                                  previewEspelho.horarios.filter(
                                                      (_, i) => i !== id,
                                                  ),
                                          })
                                          setValue('updatedAt', new Date())
                                      }}
                                  >
                                      <Trash2Icon className="text-error" />
                                  </IconButton>
                              ),
                          },
                      ]),
            ] as GridColDefPro[],
        }
    }, [lastUpdate])

    return (
        <Grid container>
            <Grid item xs={12} className="mb-4 mt-8">
                <HorizontalDivider>Espelho do ponto</HorizontalDivider>
            </Grid>
            <Grid
                item
                xs={12}
                className="bg-card shadow-md p-4 gap-2 rounded-md overflow-clip h-fit!"
            >
                <div className="w-full justify-center flex items-center">
                    <h6 className="text-center text-lg">
                        Previsualização do espelho de ponto
                    </h6>
                    <IconeAjuda>
                        É assim que a tabela de horários será exibida no pdf do
                        espelho de ponto.
                        <br />
                        Desative gerar automaticamente e clique sobre os campos
                        para editar.
                    </IconeAjuda>
                    <div style={{ flex: 1 }} />
                    <Checkbox
                        checked={!previewEspelho}
                        onCheckedChange={() => {
                            if (!previewEspelho)
                                setValue('previewEspelho', {
                                    totalSemanal,
                                    horarios: compactarJornada(horarios),
                                })
                            else setValue('previewEspelho', undefined)
                            setValue('updatedAt', new Date())
                        }}
                        containerClassName="select-none"
                        label="Gerar automaticamente"
                    />
                </div>

                <ContentGuide.DataGrid
                    key={String(memo.columns.length)}
                    loading={false}
                    className={cn({
                        'opacity-50 pointer-events-none': !previewEspelho,
                    })}
                    slots={{
                        Toolbar: () => {
                            return (
                                <div
                                    className={cn(
                                        'flex flex-row justify-between w-full h-fit',
                                        'p-2 pb-3 border border-b-0 rounded-t-md -mb-1',
                                    )}
                                >
                                    <div className="inline-flex items-center">
                                        <span className="select-none">
                                            Jornada de trabalho contratual
                                        </span>
                                        <IconButton
                                            className="ml-2 shadow-sm ring-1 ring-accent"
                                            size="small"
                                            title="Adicionar linha"
                                            onClick={() => {
                                                setValue('previewEspelho', {
                                                    ...previewEspelho,
                                                    horarios: [
                                                        ...previewEspelho!
                                                            .horarios,
                                                        {
                                                            dia: 'Novo dia',
                                                            entradasSaidas: {
                                                                Ent1: '',
                                                                Sai1: '',
                                                            },
                                                            total: '00:00',
                                                        },
                                                    ],
                                                })
                                                setValue(
                                                    'updatedAt',
                                                    new Date(),
                                                )
                                            }}
                                        >
                                            <PlusIcon className="text-success" />
                                        </IconButton>
                                    </div>
                                    {!previewEspelho ? (
                                        <span>{totalSemanal}h semanais</span>
                                    ) : (
                                        <div className="inline-flex items-center">
                                            <TextField
                                                variant="outlined"
                                                unHelperText
                                                className="w-[95px]"
                                                inputClassName="h-[25px] px-2"
                                                aria-label="Total semanal"
                                                value={
                                                    previewEspelho?.totalSemanal
                                                }
                                                endAdornment="h"
                                                options={{
                                                    blocks: [2, 2],
                                                    delimiters: [':'],
                                                    numericOnly: true,
                                                }}
                                                onChange={({
                                                    target: { value },
                                                }) => {
                                                    setValue('previewEspelho', {
                                                        ...previewEspelho,
                                                        totalSemanal: value,
                                                    })
                                                    setValue(
                                                        'updatedAt',
                                                        new Date(),
                                                    )
                                                }}
                                            />
                                            <span
                                                style={{
                                                    userSelect: 'none',
                                                }}
                                            >
                                                &nbsp;semanais
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )
                        },
                    }}
                    columns={memo.columns}
                    rows={memo.rows}
                    hideFooter
                    density="compact"
                    onCellEditCommit={
                        !previewEspelho
                            ? undefined
                            : ({ id, field, value }) => {
                                  const isHorarioValido = (
                                      value: string = '',
                                  ) => /^\d{2,3}:\d{2}$/.test(value) || !value
                                  if (field === 'dia') {
                                      setValue('previewEspelho', {
                                          ...previewEspelho,
                                          horarios: previewEspelho.horarios.map(
                                              (h, i) =>
                                                  i === id
                                                      ? {
                                                            ...h,
                                                            [field]: value,
                                                        }
                                                      : h,
                                          ),
                                      })
                                  } else if (
                                      isHorarioValido(value) &&
                                      field !== 'total'
                                  ) {
                                      setValue('previewEspelho', {
                                          ...previewEspelho,
                                          horarios: previewEspelho.horarios.map(
                                              (h, i) =>
                                                  i === id
                                                      ? {
                                                            ...h,
                                                            entradasSaidas: {
                                                                ...h.entradasSaidas,
                                                                [field]: value,
                                                            },
                                                        }
                                                      : h,
                                          ),
                                      })
                                  } else if (
                                      isHorarioValido(value) &&
                                      field === 'total'
                                  ) {
                                      setValue('previewEspelho', {
                                          ...previewEspelho,
                                          horarios: previewEspelho.horarios.map(
                                              (h, i) =>
                                                  i === id
                                                      ? {
                                                            ...h,
                                                            [field]: value,
                                                        }
                                                      : h,
                                          ),
                                      })
                                  }
                                  setValue('updatedAt', new Date())
                              }
                    }
                />
            </Grid>
        </Grid>
    )
}

function parsePreviewEspelho(value: unknown): PreviewEspelho | undefined {
    if (typeof value !== 'string') return (value as PreviewEspelho) ?? undefined
    if (!value) return undefined
    try {
        return JSON.parse(value) as PreviewEspelho
    } catch {
        return undefined
    }
}

function mapColumnsEntradasSaidas(
    previewEspelho: any,
    horarios: TabelaHorarios['horarios'],
) {
    const columns = []
    for (let i = 1; i <= 3; i++) {
        if (previewEspelho !== undefined) {
            columns.push({
                ...optionsColumn,
                editable: true,
                field: `Ent${i}`,
                headerName: 'ENT',
                flex: 1,
            })
            columns.push({
                ...optionsColumn,
                editable: true,
                field: `Sai${i}`,
                headerName: 'SAI',
                flex: 1,
            })
        } else {
            const entExists = horarios.find((h: any) => h[`Ent${i}`])
            const saiExists = horarios.find((h: any) => h[`Sai${i}`])

            if (entExists)
                columns.push({
                    ...optionsColumn,
                    editable: true,
                    field: `Ent${i}`,
                    headerName: 'ENT',
                    flex: 1,
                })
            if (saiExists)
                columns.push({
                    ...optionsColumn,
                    editable: true,
                    field: `Sai${i}`,
                    headerName: 'SAI',
                    flex: 1,
                })
        }
    }
    return columns as GridColDefPro[]
}
